import React from 'react';
import './StatusStripe.css';

/**
 * StatusStripe
 *
 * Thin proportional bar (up to 4 colored segments) showing how a claim's
 * (or a facility's) line items adjudicated: approved / pending / queried / rejected,
 * with small counts + colored dots underneath.
 *
 * Part of the claims console redesign — replaces flat status tags with real
 * cumulative line-level information wherever it's available.
 */

const SEGMENTS = [
  { key: 'approved', label: 'appr.' },
  { key: 'pending', label: 'pend.' },
  { key: 'queried', label: 'quer.' },
  { key: 'rejected', label: 'rej.' },
];

/**
 * Normalizes the two shapes the backend uses for per-claim line status into
 * a flat { approved, pending, queried, rejected } counts object:
 *  - Payment flow (getPaymentStatusClaims -> global classifyItemClaims()):
 *      { code, color, text, cat: { Approved: { count }, Pending: { count }, Queried: { count }, Declined: { count } } }
 *  - Admin flow (getAdminFacilityClaims, built inline): already flat
 *      { approved, pending, queried, rejected, code }
 */
export function normalizeAggregatedItemsStatus(agg) {
  if (!agg) return null;
  if (agg.cat) {
    const cat = agg.cat;
    return {
      approved: Number(cat.Approved?.count) || 0,
      pending: Number(cat.Pending?.count) || 0,
      queried: Number(cat.Queried?.count) || 0,
      rejected: Number(cat.Declined?.count) || 0,
    };
  }
  return {
    approved: Number(agg.approved) || 0,
    pending: Number(agg.pending) || 0,
    queried: Number(agg.queried) || 0,
    rejected: Number(agg.rejected) || 0,
  };
}

/** Builds counts from the facility/month line-aggregate fields (*_lines_count). */
export function lineCountsFromAggregate(row) {
  if (!row) return null;
  return {
    approved: Number(row.approved_lines_count) || 0,
    pending: Number(row.pending_lines_count) || 0,
    queried: Number(row.queried_lines_count) || 0,
    rejected: Number(row.rejected_lines_count) || 0,
  };
}

export default function StatusStripe({ counts, className, compact = false, showLabels = true }) {
  if (!counts) return null;
  const approved = Number(counts.approved) || 0;
  const pending = Number(counts.pending) || 0;
  const queried = Number(counts.queried) || 0;
  const rejected = Number(counts.rejected) || 0;
  const total = approved + pending + queried + rejected;

  if (total <= 0) return null;

  const values = { approved, pending, queried, rejected };

  return (
    <div className={`status-stripe${compact ? ' status-stripe--compact' : ''}${className ? ` ${className}` : ''}`}>
      <div className="status-stripe__bar">
        {SEGMENTS.map(({ key }) => {
          const value = values[key];
          if (!value) return null;
          const width = (value / total) * 100;
          return (
            <span
              key={key}
              className={`status-stripe__seg status-stripe__seg--${key}`}
              style={{ width: `${width}%` }}
            />
          );
        })}
      </div>
      <div className="status-stripe__counts">
        {SEGMENTS.map(({ key, label }) => {
          const value = values[key];
          if (!value) return null;
          return (
            <span className="status-stripe__count" key={key}>
              <span className={`status-stripe__dot status-stripe__dot--${key}`} />
              <b>{value}</b>
              {showLabels ? ` ${label}` : ''}
            </span>
          );
        })}
      </div>
    </div>
  );
}
